import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import {
  Mail,
  Clock,
  Linkedin,
  ArrowRight,
  ChevronDown,
  Facebook,
  Instagram,
  Youtube,
} from "lucide-react";
import toast from "react-hot-toast";
import ReCAPTCHA from "react-google-recaptcha";
import "./Contact.css";
import { SERVER_URL } from "../../utils/serverConfig";
import PublicHeader from "../../components/PublicHeader/PublicHeader";

const Contact = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const recaptchaRef = React.useRef(null);
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [formData, setFormData] = useState({
    inquiryType: "general",
    fullName: "",
    email: "",
    company: "",
    subject: "",
    message: "",
  });

  // Map inquiryType values to API format
  const inquiryTypeMap = {
    general: "General Inquiry",
    sales: "Sales Question",
    support: "Support Issue",
    bug: "Critical Bug",
    other: "Other",
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };
  console.log(
    SERVER_URL,
    process.env.NODE_ENV === "development"
      ? "http://52.66.103.58"
      : window.location.origin,
    "SERVER_URL"
  );

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!recaptchaToken) {
      toast.error("Please complete the reCAPTCHA verification.");
      return;
    }

    setIsSubmitting(true);

    // Push form details for GTM (Form ID, Form Text, Form URL)
    const formEl = e.target;
    const formId = formEl?.id || "contact-us-form";
    const formText = formEl?.querySelector('button[type="submit"]')?.textContent?.trim() || "Send Message";
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "form_submit",
      form_id: formId,
      formText,
      formUrl: window.location.href,
    });

    try {
      const userTimezone =
        typeof Intl !== "undefined" && Intl.DateTimeFormat?.().resolvedOptions?.().timeZone
          ? Intl.DateTimeFormat().resolvedOptions().timeZone
          : "UTC";

      const payload = {
        type: "contact_us",
        inquiryType: inquiryTypeMap[formData.inquiryType] || "General Inquiry",
        fullName: formData.fullName,
        email: formData.email,
        companyName: formData.company,
        subject: formData.subject,
        message: formData.message,
        userTimezone,
        recaptchaToken,
      };

      const response = await fetch(
        `${
          process.env.NODE_ENV === "development"
            ? "http://52.66.103.58"
            : 
            window.location.origin
        }/api/v1/contact`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        
        // Build user-friendly error message
        let errorMessage = errorData.message || `HTTP error! status: ${response.status}`;
        
        // If there are validation details, add specific field errors
        if (errorData.details && typeof errorData.details === 'object') {
          const fieldErrors = Object.values(errorData.details);
          if (fieldErrors.length > 0) {
            const specificErrors = fieldErrors.join('. ');
            errorMessage = `${errorMessage} ${specificErrors}`;
          }
        }
        
        throw new Error(errorMessage);
      }

      const data = await response.json();
      toast.success("Message sent successfully!");
      // Navigate to thank you page after a short delay
      setTimeout(() => {
        navigate("/thanks-contact-us");
      }, 500);
    } catch (error) {
      console.error("Error submitting contact form:", error);
      toast.error(error.message || "Failed to send message. Please try again.");
    } finally {
      setIsSubmitting(false);
      // Reset reCAPTCHA so user can retry
      setRecaptchaToken(null);
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
    }
  };


  return (
    <>
      <Helmet>
        <title>Contact Us | TeamGrid</title>
        <meta name="description" content="Get in touch with TeamGrid. Questions about workforce intelligence, demos, or support? We're here to help." />
        <link rel="canonical" href="https://teamgrid.ai/contact-us" />
      </Helmet>
      <div className="contact-container">
      <div className="relative min-h-screen overflow-hidden bg-white">
        <PublicHeader />
        <div className="pt-16 sm:pt-18 md:pt-20">
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            {/* Header Section: padding 80px, centered, Inter 62px/60px title, 20px/28px subtitle */}
            <section
              className="flex flex-col justify-center items-center w-full bg-white pt-20 pb-8"
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="flex flex-col items-center w-full max-w-[1760px] gap-5"
              >
                <h1
                  className="text-center text-black"
                  style={{
                    fontFamily: "Inter, sans-serif",
                    fontWeight: 800,
                    fontSize: 62,
                    lineHeight: "60px",
                  }}
                >
                  Get in <span className="text-[#00BFA6]">Touch</span>
                </h1>
                <div className="flex flex-col items-center w-full max-w-[672px]">
                  <p
                    className="text-center"
                    style={{
                      fontFamily: "Inter, sans-serif",
                      fontWeight: 400,
                      fontSize: 20,
                      lineHeight: "28px",
                      color: "rgba(0, 0, 0, 0.6)",
                    }}
                  >
                    Have a question or need help? We're here for you. Our team
                    typically responds within 24 hours.
                  </p>
                </div>
              </motion.div>
            </section>

            {/* Main Content */}
            <section className="pt-8 pb-16 bg-white">
                <div className="w-full">
                  {/* Two Column Layout */}
                  <div className="grid lg:grid-cols-[minmax(0,400px)_1fr] gap-8">
                    {/* Left Column - Information Panels */}
                    <motion.div
                      initial={{ opacity: 0, x: -30 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.8, delay: 0.2 }}
                      className="space-y-6 max-w-[400px] lg:max-w-none"
                    >
                      {/* Email Us Card */}
                      <div className="relative rounded-2xl p-6 contact-glass">
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 rounded-lg bg-[#E0FFFC] flex items-center justify-center flex-shrink-0">
                            <Mail className="w-6 h-6 text-[#00BFA6]" />
                          </div>
                          <div className="flex-1">
                            <h3 className="text-lg font-semibold text-black mb-4">
                              Email Us
                            </h3>
                            <div>
                              <p className="text-sm text-gray-600 mb-1">
                                For General Inquiries Email us at :
                              </p>
                              <a
                                href="mailto:hello@teamgrid.ai"
                                className="text-[#00BFA6] hover:underline font-medium"
                              >
                                hello@teamgrid.ai
                              </a>
                              <p className="text-sm text-gray-600 mb-1 mt-4">
                                For Support Email us at :
                              </p>
                              <a
                                href="mailto:support@teamgrid.ai"
                                className="text-[#00BFA6] hover:underline font-medium"
                              >
                                support@teamgrid.ai
                              </a>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Response Times Card */}
                      <div className="relative rounded-2xl p-6 contact-glass">
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 rounded-lg bg-[#EDF4FF] flex items-center justify-center flex-shrink-0">
                            <Clock className="w-6 h-6 text-[#366EB6]" />
                          </div>
                          <div className="flex-1">
                            <h3 className="text-lg font-semibold text-black mb-4">
                              Response Times
                            </h3>
                            <ul className="space-y-3">
                              {[
                                { label: "General inquiries", value: "24 hours", dot: "bg-green-500" },
                                { label: "Sales questions", value: "12 hours", dot: "bg-blue-500" },
                                { label: "Critical Support", value: "1 hour", dot: "bg-orange-500" },
                              ].map((item, idx) => (
                                <li key={idx} className="flex items-start gap-3">
                                  <div className={`w-2 h-2 rounded-full ${item.dot} mt-2 flex-shrink-0`}></div>
                                  <span className="text-sm text-gray-600">
                                    {item.label}: <strong>{item.value}</strong>
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>

                      {/* Connect With Us Card */}
                      <div className="relative rounded-2xl p-6 contact-glass">
                        <h3 className="text-lg font-semibold text-black mb-4">
                          Connect With Us
                        </h3>
                        <div className="flex gap-3">
                          <a
                            href="https://www.linkedin.com/company/teamgridai/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-10 h-10 rounded-lg bg-gray-100 border border-gray-200 flex items-center justify-center text-gray-600 hover:text-[#00BFA6] hover:border-[#00BFA6]/30 transition-all"
                          >
                            <Linkedin className="w-5 h-5" />
                          </a>
                          <a
                            href="https://www.instagram.com/teamgrid.ai/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-10 h-10 rounded-lg bg-gray-100 border border-gray-200 flex items-center justify-center text-gray-600 hover:text-[#00BFA6] hover:border-[#00BFA6]/30 transition-all"
                          >
                            <Instagram className="w-5 h-5" />
                          </a>
                          <a
                            href="https://www.facebook.com/teamgridai"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-10 h-10 rounded-lg bg-gray-100 border border-gray-200 flex items-center justify-center text-gray-600 hover:text-[#00BFA6] hover:border-[#00BFA6]/30 transition-all"
                          >
                            <Facebook className="w-5 h-5" />
                          </a>
                          <a
                            href="https://www.youtube.com"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-10 h-10 rounded-lg bg-gray-100 border border-gray-200 flex items-center justify-center text-gray-600 hover:text-[#00BFA6] hover:border-[#00BFA6]/30 transition-all"
                          >
                            <Youtube className="w-5 h-5" />
                          </a>
                        </div>
                      </div>
                    </motion.div>

                    {/* Right Column - Contact Form */}
                    <motion.div
                      initial={{ opacity: 0, x: 30 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.8, delay: 0.4 }}
                    >
                      <div className="relative rounded-3xl p-8 contact-glass">
                        <h3 className="text-lg font-semibold text-black mb-6">
                          Send us a message
                        </h3>

                        <form
                          id="contact-us-form"
                          onSubmit={handleSubmit}
                          className="flex flex-col items-start p-0 gap-5 w-full max-w-[670px] flex-none grow-0 z-[2]"
                          style={{ fontFamily: "Inter, sans-serif" }}
                        >
                          {/* Topic Selection */}
                          <div className="flex flex-col items-start p-0 gap-2 w-full max-w-[670px] flex-none order-0 self-stretch grow-0">
                            <label className="block w-full h-5 text-sm font-semibold leading-5 text-[#475569]">
                              What can we help you with?
                            </label>
                            <div className="relative flex flex-col items-stretch w-full flex-none order-1 self-stretch grow-0">
                              <select
                                name="inquiryType"
                                value={formData.inquiryType}
                                onChange={handleInputChange}
                                className="w-full h-[50px] py-3 pl-4 pr-12 bg-white border border-[#EDF4FF] rounded-xl text-[16px] font-medium leading-6 text-[#475569] focus:outline-none focus:border-[#00BFA6] focus:ring-1 focus:ring-[#00BFA6] transition-all box-border appearance-none cursor-pointer"
                                required
                              >
                                <option value="general">I have a general inquiry</option>
                                <option value="sales">Sales Question</option>
                                <option value="support">Support Issue</option>
                                <option value="bug">Critical Bug</option>
                                <option value="other">Other</option>
                              </select>
                              <span className="absolute right-5 top-1/2 -translate-y-1/2 pointer-events-none flex items-center justify-center text-[#A0A0A0]">
                                <ChevronDown className="w-5 h-5" strokeWidth={2.5} />
                              </span>
                            </div>
                          </div>

                          {/* Name + Email row */}
                          <div className="flex flex-row justify-center items-start gap-6 w-full max-w-[670px] flex-none order-1 self-stretch grow-0">
                            <div className="flex flex-col items-start p-0 gap-2 flex-1 min-w-0">
                              <label className="block w-full text-sm font-semibold leading-5 text-[#475569]">
                                Full Name *
                              </label>
                              <input
                                type="text"
                                name="fullName"
                                value={formData.fullName}
                                onChange={handleInputChange}
                                className="w-full h-[49px] py-3.5 pl-10 pr-4 bg-white border border-[#EDF4FF] rounded-xl text-base text-[#475569] placeholder:text-[#A0A0A0] focus:outline-none focus:border-[#00BFA6] focus:ring-1 focus:ring-[#00BFA6] transition-all box-border"
                                placeholder="John Doe"
                                required
                              />
                            </div>
                            <div className="flex flex-col items-start p-0 gap-2 flex-1 min-w-0">
                              <label className="block w-full text-sm font-semibold leading-5 text-[#475569]">
                                Work Email *
                              </label>
                              <input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleInputChange}
                                className="w-full h-[49px] py-3.5 pl-10 pr-4 bg-white border border-[#EDF4FF] rounded-xl text-base text-[#475569] placeholder:text-[#A0A0A0] focus:outline-none focus:border-[#00BFA6] focus:ring-1 focus:ring-[#00BFA6] transition-all box-border"
                                placeholder="john@company.com"
                                required
                              />
                            </div>
                          </div>

                          {/* Company + Subject row */}
                          <div className="flex flex-row justify-center items-start gap-6 w-full max-w-[670px] flex-none order-2 self-stretch grow-0">
                            <div className="flex flex-col items-start p-0 gap-2 flex-1 min-w-0">
                              <label className="block w-full text-sm font-semibold leading-5 text-[#475569]">
                                Company Name *
                              </label>
                              <input
                                type="text"
                                name="company"
                                value={formData.company}
                                onChange={handleInputChange}
                                className="w-full h-[49px] py-3.5 pl-10 pr-4 bg-white border border-[#EDF4FF] rounded-xl text-base text-[#475569] placeholder:text-[#A0A0A0] focus:outline-none focus:border-[#00BFA6] focus:ring-1 focus:ring-[#00BFA6] transition-all box-border"
                                placeholder="Acme Inc."
                                required
                              />
                            </div>
                            <div className="flex flex-col items-start p-0 gap-2 flex-1 min-w-0">
                              <label className="block w-full text-sm font-semibold leading-5 text-[#475569]">
                                Subject *
                              </label>
                              <input
                                type="text"
                                name="subject"
                                value={formData.subject}
                                onChange={handleInputChange}
                                className="w-full h-[49px] py-3.5 px-4 bg-white border border-[#EDF4FF] rounded-xl text-base text-[#475569] placeholder:text-[#A0A0A0] focus:outline-none focus:border-[#00BFA6] focus:ring-1 focus:ring-[#00BFA6] transition-all box-border"
                                placeholder="How can we help?"
                                required
                              />
                            </div>
                          </div>

                          {/* Message */}
                          <div className="flex flex-col items-start px-0 pt-0 pb-1.5 gap-2 w-full max-w-[670px] flex-none order-3 self-stretch grow-0">
                            <label className="block w-full text-sm font-semibold leading-5 text-[#475569]">
                              Message *
                            </label>
                            <textarea
                              name="message"
                              value={formData.message}
                              onChange={handleInputChange}
                              className="w-full min-h-[122px] py-3 px-4 pb-[84px] bg-white border border-[#EDF4FF] rounded-xl text-base leading-6 text-[#475569] placeholder:text-[#A0A0A0] focus:outline-none focus:border-[#00BFA6] focus:ring-1 focus:ring-[#00BFA6] transition-all resize-none overflow-auto box-border"
                              placeholder="Tell us more about your inquiry..."
                              required
                            />
                          </div>

                          {/* reCAPTCHA */}
                          <div className="flex flex-col items-start p-0 w-full max-w-[670px] flex-none order-4 self-stretch grow-0">
                            <ReCAPTCHA
                              ref={recaptchaRef}
                              sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                              onChange={(token) => setRecaptchaToken(token)}
                              onExpired={() => setRecaptchaToken(null)}
                            />
                          </div>

                          {/* Submit */}
                          <div className="flex flex-col items-start p-0 gap-[15.5px] w-full max-w-[670px] flex-none order-5 self-stretch grow-0">
                            <button
                              type="submit"
                              disabled={isSubmitting || !recaptchaToken}
                              className="w-full h-[60px] py-4 flex flex-row justify-center items-center gap-2 bg-[#00D1C1] rounded-xl font-bold text-lg leading-7 text-black disabled:opacity-50 disabled:cursor-not-allowed transition-all hover:opacity-90"
                              style={{
                                boxShadow: "0px 0px 20px rgba(0, 209, 193, 0.2)",
                              }}
                            >
                              {isSubmitting ? "Sending..." : "Send Message"}
                              {!isSubmitting && (
                                <ArrowRight className="w-5 h-5" />
                              )}
                            </button>
                            <p className="w-full text-center text-xs leading-4 text-[#A0A0A0]">
                              By submitting this form, you agree to our{" "}
                              <a
                                href="/privacy-policy"
                                className="underline text-[#A0A0A0] hover:opacity-80"
                              >
                                Privacy Policy
                              </a>
                              .
                            </p>
                          </div>
                        </form>
                      </div>
                    </motion.div>
                  </div>
                </div>
              </section>

            {/* Bottom footer */}
            <footer
              className="flex flex-col items-center border-t py-6 px-4 sm:px-6 md:px-8 lg:px-[344px]"
              style={{
                borderTopColor: "rgba(0, 0, 0, 0.2)",
                fontFamily: "Inter, sans-serif",
              }}
            >
              <div className="flex flex-row flex-wrap justify-center items-center gap-x-6 gap-y-2 max-w-[1232px] w-full">
                <a href="/security" className="text-black text-sm font-normal hover:underline" style={{ lineHeight: "20px", fontSize: 14 }}>
                  Security
                </a>
                <a href="/privacy-policy" className="text-black text-sm font-normal hover:underline" style={{ lineHeight: "20px", fontSize: 14 }}>
                  Privacy
                </a>
                <a href="/terms" className="text-black text-sm font-normal hover:underline" style={{ lineHeight: "20px", fontSize: 14 }}>
                  Terms
                </a>
                <a href="/contact-us" className="text-black text-sm font-normal hover:underline" style={{ lineHeight: "20px", fontSize: 14 }}>
                  Contact
                </a>
                <span className="text-black text-sm font-normal" style={{ lineHeight: "20px", fontSize: 14 }}>
                  © 2026 TeamGrid
                </span>
              </div>
            </footer>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default Contact;
